import {ActionPlanImpactLevel, CategoryKeyEnum} from '#apollo/schema';
import {ActionPlanFilters} from '#component/modal/ActionPlanFilterModal';
import {ActionPlanPage} from '#component/page/ActionPlan/ActionPlan.types';

import {Action, SortDirection, SortType} from './RecommendationTable.types';

const impactLevelSortOrder = {
  [ActionPlanImpactLevel.High]: 0,
  [ActionPlanImpactLevel.Medium]: 1,
  [ActionPlanImpactLevel.Low]: 2,
};

const categorySortOrder = {
  [CategoryKeyEnum.Diet]: 0,
  [CategoryKeyEnum.Fitness]: 1,
  [CategoryKeyEnum.Sleep]: 2,
  [CategoryKeyEnum.Mental]: 3,
  [CategoryKeyEnum.Habits]: 4,
  [CategoryKeyEnum.Research]: 5,
  [CategoryKeyEnum.You]: 6,
};

export const initialState = {
  sortDirection: 'asc' as SortDirection,
  sortType: 'default' as SortType,
} as const;

/**
 * This is a reducer function that manages the sort order and type for the
 * table.
 */
export function reducer(state = initialState, action: Action): typeof initialState {
  switch (action.type) {
    case 'SetSortType':
      return state.sortType === action.payload
        ? {...state, sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc'}
        : {...state, sortDirection: 'asc', sortType: action.payload};
    default:
      return state;
  }
}

/**
 * This function returns a number indicating whether a given recommendation's
 * `impactLevel` comes before, after, or is the same as another in the sort
 * order.
 */
function compareImpactLevel(a: ActionPlanImpactLevel, b: ActionPlanImpactLevel) {
  return Math.min(Math.max(impactLevelSortOrder[a] - impactLevelSortOrder[b], -1), 1);
}

/**
 * This function returns a sorted list of recommendations based on a given
 * sort direction and type.
 */
export function getSortedRecommendations(
  recommendations: ActionPlanPage.ActionPlanAdjustment[] = [],
  sortDirection: SortDirection,
  sortType: SortType,
) {
  const defaultImpactLevel = ActionPlanImpactLevel.Low;
  const defaultCategoryKey = CategoryKeyEnum.Research;

  return [...recommendations].sort((a, b) => {
    const impactA = a.impactLevel || defaultImpactLevel;
    const impactB = b.impactLevel || defaultImpactLevel;

    if (sortType === 'default') {
      if (a.selected) {
        return b.selected ? compareImpactLevel(impactA, impactB) : -1;
      }
      return b.selected ? 1 : compareImpactLevel(impactA, impactB);
    }

    const sorter = sortDirection === 'asc' ? a : b;
    const sortee = sortDirection === 'asc' ? b : a;

    switch (sortType) {
      case 'category':
        const sorterCategoryKey =
          sorter.healthFactorAssessment?.categoryAssessment?.category?.key || defaultCategoryKey;
        const sorteeCategoryKey =
          sortee.healthFactorAssessment?.categoryAssessment?.category?.key || defaultCategoryKey;

        return Math.min(
          Math.max(categorySortOrder[sorterCategoryKey] - categorySortOrder[sorteeCategoryKey], -1),
          1,
        );
      case 'impactLevel':
        const sorterImpact = sorter.impactLevel || defaultImpactLevel;
        const sorteeImpact = sortee.impactLevel || defaultImpactLevel;
        return compareImpactLevel(sorterImpact, sorteeImpact);
      case 'title':
      default:
        return (sorter.recommendedResponse || '').localeCompare(sortee.recommendedResponse || '');
    }
  });
}

export function getFilteredRecommendations(
  filters: ActionPlanFilters,
  recommendations: ActionPlanPage.ActionPlanAdjustment[] = [],
) {
  return recommendations.filter(recommendation => {
    const categoryId = recommendation.healthFactorAssessment?.categoryAssessment?.category?.id;
    const impactLevel = recommendation.impactLevel;

    if (filters.categories.length && !filters.categories.includes(categoryId || '')) {
      return false;
    }

    if (filters.impactLevels.length && !filters.impactLevels.includes(impactLevel || '')) {
      return false;
    }

    return true;
  });
}
